/**
 * Generate Video canonical connection helpers — logical edge identity,
 * conflict stripping, and dev diagnostics.
 *
 * VISTOERA_GENERATE_VIDEO_CANONICAL_INPUT_PORTS_EDGE_DEDUP_AND_DOM_ANCHORED_HANDLE_REBUILD_P0_V2
 * §6/§7/§9/§10/§21
 */

import { addEdge, type Connection, type Edge } from '@xyflow/react'
import {
  GENERIC_INPUT_HANDLE,
  VIDEO_ORIGINAL_PROMPT_IN_HANDLE,
  isPromptEnhancementHandle,
  isVideoAudioReferenceHandle,
  isVideoKeyframeHandle,
  isVideoReferenceVideoHandle,
  isVideoStructuredAssetHandle,
  parseVideoStructuredAssetHandle,
} from './dataflow'
import { isPromptEnhancementFieldBindingRelation } from './videoPromptEnhancement'

export type CanvasEdgeLike = Pick<Edge, 'id' | 'source' | 'target' | 'sourceHandle' | 'targetHandle' | 'data'>

export interface LogicalEdgeKeyParts {
  source: string
  target: string
  semanticPort: string
}

/** Stable logical identity for Generate Video semantic connections. */
export function getCanvasLogicalEdgeKey(edge: CanvasEdgeLike): string | null {
  const port = resolveGenerateVideoSemanticPort(edge.targetHandle)
  if (!port) return null
  const assetScope = parseVideoStructuredAssetHandle(edge.targetHandle)?.keyframeId
  return `${edge.source}::${edge.target}::${port}${assetScope ? `::${assetScope}` : ''}`
}

/** Map a persisted target handle to its canonical semantic port. */
export function resolveGenerateVideoSemanticPort(targetHandle: string | null | undefined): string | null {
  if (!targetHandle) return GENERIC_INPUT_HANDLE
  if (targetHandle === GENERIC_INPUT_HANDLE) return GENERIC_INPUT_HANDLE
  if (targetHandle === VIDEO_ORIGINAL_PROMPT_IN_HANDLE) return VIDEO_ORIGINAL_PROMPT_IN_HANDLE
  if (isPromptEnhancementHandle(targetHandle)) return targetHandle
  if (isVideoKeyframeHandle(targetHandle)) return targetHandle
  if (isVideoReferenceVideoHandle(targetHandle)) return targetHandle
  if (isVideoAudioReferenceHandle(targetHandle)) return targetHandle
  if (isVideoStructuredAssetHandle(targetHandle)) return targetHandle
  return null
}

function sameLogicalBinding(a: CanvasEdgeLike, b: Pick<Connection, 'source' | 'target' | 'targetHandle'>): boolean {
  if (a.source !== b.source || a.target !== b.target) return false
  const portA = resolveGenerateVideoSemanticPort(a.targetHandle)
  const portB = resolveGenerateVideoSemanticPort(b.targetHandle)
  if (!portA || !portB || portA !== portB) return false
  const scopedA = parseVideoStructuredAssetHandle(a.targetHandle)?.keyframeId
  const scopedB = parseVideoStructuredAssetHandle(b.targetHandle)?.keyframeId
  return (scopedA ?? '') === (scopedB ?? '')
}

/**
 * Before adding a new semantic edge, remove legacy generic duplicates and
 * any prior edge for the same (source, target, semanticPort) tuple.
 */
export function stripConflictingGenerateVideoEdges<T extends CanvasEdgeLike>(
  edges: T[],
  connection: Pick<Connection, 'source' | 'target' | 'targetHandle'>,
  targetNodeType: string,
): T[] {
  if (targetNodeType !== 'generate_video') return edges
  const newPort = resolveGenerateVideoSemanticPort(connection.targetHandle)
  if (!newPort) return edges

  return edges.filter((e) => {
    if (e.target !== connection.target) return true
    // Replacing same-slot keyframe / reference-video occupant (existing behavior).
    if (
      e.targetHandle === connection.targetHandle &&
      (isVideoKeyframeHandle(connection.targetHandle) || isVideoReferenceVideoHandle(connection.targetHandle))
    ) {
      return false
    }
    // Same source feeding the same semantic port via a different handle id
    // (e.g. generic image-ref-in + scene-in) — keep only the new connection.
    if (e.source === connection.source && sameLogicalBinding(e, connection)) {
      return false
    }
    // New semantic port supersedes a legacy generic edge from the same source.
    if (
      e.source === connection.source &&
      e.targetHandle === GENERIC_INPUT_HANDLE &&
      newPort !== GENERIC_INPUT_HANDLE &&
      !isPromptEnhancementFieldBindingRelation((e.data as { relationType?: string } | undefined)?.relationType)
    ) {
      return false
    }
    return true
  })
}

/**
 * §九 "建立 commitVideoInputConnection()" — the ONE mutation boundary for
 * a `generate_video` semantic input connection, whether it came from a
 * Handle-precise drag, the large drop-zone fallback, or the
 * ACTIVE_DROP_TARGET override above. Combines: (1) canonical
 * `targetHandle` = `portId` (never re-derived from geometry), (2) strip
 * conflicting legacy-generic/duplicate-slot edges, (3) add exactly ONE
 * new edge. Callers only ever need to run `assertCommittedDropMatchesPort`
 * afterward — no separate strip+addEdge choreography at each call site.
 */
export function commitVideoInputConnection<T extends CanvasEdgeLike>(
  edges: T[],
  connection: Pick<Connection, 'source' | 'target' | 'sourceHandle' | 'targetHandle'>,
  targetNodeType: string,
  buildEdge: (connection: Pick<Connection, 'source' | 'target' | 'sourceHandle' | 'targetHandle'>) => T,
): T[] {
  const base = stripConflictingGenerateVideoEdges(edges, connection, targetNodeType)
  return addEdge(buildEdge(connection) as unknown as Edge, base as unknown as Edge[]) as unknown as T[]
}

export function findDuplicateLogicalGenerateVideoEdges(edges: CanvasEdgeLike[]): string[] {
  const seen = new Map<string, string>()
  const dupes: string[] = []
  for (const e of edges) {
    const key = getCanvasLogicalEdgeKey(e)
    if (!key) continue
    const prior = seen.get(key)
    if (prior) {
      dupes.push(`${prior},${e.id}`)
    } else {
      seen.set(key, e.id)
    }
  }
  return dupes
}

export interface CanvasEdgeDiagnosticPayload {
  sourceNode: string
  sourceHandle?: string | null
  targetNode: string
  targetHandle?: string | null
  semanticPort: string | null
  logicalEdgeKey: string | null
  edgeId?: string
  createdBy: string
}

export function logCanvasEdgeDiagnostic(payload: CanvasEdgeDiagnosticPayload): void {
  if (!import.meta.env?.DEV) return
  if (typeof window === 'undefined') return
  const flag = (window as unknown as { __PCV_EDGE_DIAG__?: boolean }).__PCV_EDGE_DIAG__
  if (!flag) return
  console.info('[pcv-edge-diag]', payload)
}

/**
 * VISTOERA_GENERATE_VIDEO_DROP_ZONE_IS_TARGET_PORT_AND_COLLAPSED_VISUAL_PROXY_P0_V3
 * §一/§三/§四 — DROP ZONE IS THE TARGET PORT.
 *
 * Root cause of "Product 高亮正确，但线连到别的地方": React Flow's OWN
 * internal connection engine (`@xyflow/system`) independently computes
 * a "nearest candidate handle" from raw DOM `<Handle>` geometry —
 * completely unaware of our large semantic drop-zone rectangles (a
 * different, coarser hit-test). Whenever that nearest-handle search
 * lands within its `connectionRadius` of ANY real Handle (even the
 * WRONG category's tiny dot, since Character/Product/Scene/Reference
 * rows now sit close together), XYFlow marks the connection
 * `isValid=true` and calls the app's `onConnect` DIRECTLY with THAT
 * handle — never consulting `resolveGenerateVideoDropTarget` at all.
 * `CanvasBoard.tsx`'s old `onConnectEnd` fallback only ran when XYFlow
 * found NOTHING (`isValid=false`), so a "close but wrong" native match
 * silently overwrote the semantic target the user's pointer was
 * actually over.
 *
 * Fix: track the SAME large-rectangle hit test result the visual
 * highlight already computes (`resolveGenerateVideoDropTarget`) in one
 * module-level ref, authoritative for the CURRENT drag gesture. Every
 * `onConnect` call (whether fired natively by XYFlow's own nearest-
 * handle match, or synthesized by our drop-zone fallback) consults this
 * ref FIRST and — if it names the SAME (source, target) pair — force-
 * overwrites `targetHandle` to the tracked port. XYFlow's own handle
 * pick becomes advisory only; ACTIVE_DROP_TARGET is authoritative. */
export interface ActiveVideoDropTarget {
  sourceNodeId: string
  targetNodeId: string
  port: string
}

let activeVideoDropTarget: ActiveVideoDropTarget | null = null

export function setActiveVideoDropTarget(next: ActiveVideoDropTarget | null): void {
  activeVideoDropTarget = next
}

export function getActiveVideoDropTarget(): ActiveVideoDropTarget | null {
  return activeVideoDropTarget
}

export function clearActiveVideoDropTarget(): void {
  activeVideoDropTarget = null
}

/**
 * §四 "禁止 pointer-up 二次 target resolution" — pure override: when the
 * tracked ACTIVE_DROP_TARGET names the SAME source/target pair as the
 * connection XYFlow (or our own fallback) is about to commit, the
 * tracked `port` wins unconditionally — never a nearest-handle guess,
 * never a generic/legacy handle, never the raw `connection.targetHandle`
 * XYFlow itself proposed. Returns the (possibly rewritten) connection
 * plus whether an override actually happened, so `onConnect` can log +
 * assert the invariant without a second lookup.
 */
export function applyActiveVideoDropTargetOverride<
  T extends { source: string | null; target: string | null; targetHandle?: string | null },
>(connection: T, targetNodeType: string | null | undefined): { connection: T; overridden: boolean; port: string | null } {
  const active = activeVideoDropTarget
  if (
    !active ||
    targetNodeType !== 'generate_video' ||
    active.sourceNodeId !== connection.source ||
    active.targetNodeId !== connection.target
  ) {
    return { connection, overridden: false, port: null }
  }
  if (connection.targetHandle === active.port) {
    return { connection, overridden: false, port: active.port }
  }
  return { connection: { ...connection, targetHandle: active.port }, overridden: true, port: active.port }
}

/**
 * §十七 强 invariant — the drop zone that was highlighted during the
 * drag MUST equal the port actually committed to the persisted edge.
 * DEV-only: never throws in production, never blocks the commit (the
 * override above already guarantees this holds structurally); this is
 * a regression trip-wire only.
 */
/** Pure predicate — never gated on `import.meta.env`, so it stays
 * trivially testable under plain Node (no Vite runtime) as well as in
 * the browser. */
export function dropMatchesCommittedPort(highlightedPort: string | null, committedTargetHandle: string | null | undefined): boolean {
  if (!highlightedPort) return true
  return highlightedPort === committedTargetHandle
}

export function assertCommittedDropMatchesPort(highlightedPort: string | null, committedTargetHandle: string | null | undefined): void {
  if (!import.meta.env?.DEV) return
  if (!dropMatchesCommittedPort(highlightedPort, committedTargetHandle)) {
    // eslint-disable-next-line no-console
    console.error('[pcv-drop-zone-invariant] highlighted drop zone port did not match committed edge target handle', {
      highlightedPort,
      committedTargetHandle,
    })
  }
}
